import { useTranslate } from "@refinedev/core";
import { Timeline, TimelineItem, type TimelineSection } from "@libertify/ui";
import { Button, Empty, Flex, Spin } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo } from "react";
import { ViewerActivityEvent } from "types";
import { ACTIVITY_TIMELINE_PAGE_SIZE } from "pages/media/constants";
import { useActivityTimeline } from "../../projects/hooks/useActivityTimeline";
import { getActivityDayGroupLabel } from "../../projects/utils/activityDayGroupLabel";
import {
  getActivityEventDescription,
  getActivityEventTone,
} from "../../projects/utils/activityEventCopy";
import { getViewerDisplayName } from "../../projects/utils/viewerDisplayName";
import { SectionHeader } from "./SectionHeader";
import { PREFIXES } from "./constants/constants";

dayjs.extend(relativeTime);

export const ActivityTimeline = ({
  projectId,
  startDate,
  endDate,
}: {
  projectId: string;
  startDate?: string;
  endDate?: string;
}) => {
  const t = useTranslate();
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useActivityTimeline({
      projectId,
      params: {
        page: 1,
        size: ACTIVITY_TIMELINE_PAGE_SIZE,
        from_date: startDate,
        to_date: endDate,
      },
    });

  const events = useMemo(
    () =>
      data?.reduce<ViewerActivityEvent[]>(
        (acc, page) => [...acc, ...page.data],
        []
      ) ?? [],
    [data]
  );

  const sections = useMemo<TimelineSection[]>(() => {
    const buckets = new Map<string, ViewerActivityEvent[]>();
    events.forEach((event) => {
      const day = dayjs(event.event_at).startOf("day");
      const key = day.format("YYYY-MM-DD");
      buckets.set(key, [...(buckets.get(key) ?? []), event]);
    });

    return Array.from(buckets.entries()).map(([dayKey, dayEvents]) => ({
      key: dayKey,
      label: getActivityDayGroupLabel(dayjs(dayKey), t),
      children: dayEvents.map((event, index) => (
        <TimelineItem
          key={`${event.session_id}-${event.event_at}-${index}`}
          title={getViewerDisplayName(event, t)}
          description={getActivityEventDescription(event, t)}
          timestamp={dayjs(event.event_at).fromNow()}
          tone={getActivityEventTone(event)}
        />
      )),
    }));
  }, [events, t]);

  return (
    <Flex>
      <Flex vertical gap={20} style={{ width: "50%" }}>
        <SectionHeader type="activity" />
        {isLoading ? (
          <Flex align="center" justify="center">
            <Spin />
          </Flex>
        ) : (
          <>
            <Timeline
              sections={sections}
              emptyState={
                <Empty description={t(`${PREFIXES.activity}.noActivity`)} />
              }
            />
            {hasNextPage && (
              <Flex justify="center">
                <Button
                  loading={isFetching}
                  onClick={() => {
                    fetchNextPage().catch((error) =>
                      console.error("Error fetching next page", error)
                    );
                  }}
                >
                  {t(`${PREFIXES.activity}.loadMore`)}
                </Button>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
